import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Activity01Icon,
  Add01Icon,
  AlertCircleIcon,
  CloudServerIcon,
  Database01Icon,
  DollarCircleIcon,
  Key01Icon,
  Rocket01Icon,
  Search01Icon,
  SecurityCheckIcon,
  UserMultipleIcon,
  WorkflowCircle06Icon,
} from '@hugeicons/core-free-icons';
import { toast } from 'sonner';

import { useAuth } from '@/context/AuthContext';
import { api, apiError } from '@/lib/api';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { EnvironmentBadge, EnvironmentBadges, StatusBadge } from '@/components/applications/ApplicationBadges';

const EMPTY_SUMMARY = {
  total_applications: 0,
  applications_with_alert: 0,
  applications_without_owner: 0,
  pending_approvals: 0,
  active_access_sessions: 0,
  recent_deployments: 0,
  estimated_monthly_cost: 0,
};

export default function ApplicationPlatform() {
  const { can } = useAuth();
  const [summary, setSummary] = useState(null);
  const [applications, setApplications] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [query, setQuery] = useState('');
  const [initiativeFilter, setInitiativeFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const requests = [api.get('/applications/summary'), api.get('/applications'), api.get('/initiatives')];
      if (can.databases) requests.push(api.get('/database-resources'));
      if (can.reviewAccess) requests.push(api.get('/access-requests'));
      const results = await Promise.all(requests);
      setSummary(results[0].data || EMPTY_SUMMARY);
      setApplications(Array.isArray(results[1].data) ? results[1].data : []);
      const initiativeData = results[2].data;
      setInitiatives(Array.isArray(initiativeData) ? initiativeData : initiativeData?.items || []);
      let index = 3;
      if (can.databases) setDatabases(Array.isArray(results[index++].data) ? results[index - 1].data : []);
      if (can.reviewAccess) setAccessRequests(Array.isArray(results[index].data) ? results[index].data : []);
    } catch (requestError) {
      setError(apiError(requestError));
    } finally {
      setLoading(false);
    }
  }, [can.databases, can.reviewAccess]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredApplications = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return applications.filter((application) => {
      const matchesQuery =
        !normalized ||
        [application.name, application.application_key, application.initiative_name, application.owner_name].some(
          (value) => value?.toLowerCase().includes(normalized),
        );
      return (
        matchesQuery &&
        (initiativeFilter === 'all' || application.initiative_id === initiativeFilter) &&
        (ownerFilter === 'all' || application.owner_user_id === ownerFilter) &&
        (environmentFilter === 'all' ||
          application.environments?.some((item) => item.environment === environmentFilter)) &&
        (statusFilter === 'all' || application.status === statusFilter)
      );
    });
  }, [applications, environmentFilter, initiativeFilter, ownerFilter, query, statusFilter]);

  const ownerOptions = useMemo(
    () =>
      applications
        .filter((item) => item.owner_user_id)
        .reduce((options, item) => {
          if (!options.some((option) => option.id === item.owner_user_id))
            options.push({ id: item.owner_user_id, name: item.owner_name });
          return options;
        }, []),
    [applications],
  );

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-6 py-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
            <HugeiconsIcon icon={WorkflowCircle06Icon} size={15} aria-hidden="true" />
            Operación restringida
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight md:text-3xl">Gestión de Aplicaciones</h1>
          <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">
            Operación técnica de iniciativas previamente inscritas en el Registro y evaluadas antes de recibir una base
            de datos.
          </p>
        </div>
        {can.manageApplications && (
          <Button asChild>
            <Link to="/aplicaciones-ia/nueva">
              <HugeiconsIcon icon={Add01Icon} data-icon="inline-start" aria-hidden="true" />
              Nueva aplicación
            </Link>
          </Button>
        )}
      </div>

      <Alert>
        <HugeiconsIcon icon={SecurityCheckIcon} aria-hidden="true" />
        <AlertTitle>Registro y evaluación obligatorios</AlertTitle>
        <AlertDescription>
          Esta vista no crea iniciativas. Primero deben inscribirse en{' '}
          <Link to="/registro" className="font-semibold underline">
            AI Registry
          </Link>{' '}
          y completar su evaluación de riesgo. Sin ese resultado, VIA bloquea el aprovisionamiento y el acceso a bases
          de datos.
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive">
          <HugeiconsIcon icon={AlertCircleIcon} size={16} aria-hidden="true" />
          <AlertTitle>No fue posible cargar la plataforma</AlertTitle>
          <AlertDescription>{error}. Verifica que las migraciones de la base VIA estén aplicadas.</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <LoadingState />
      ) : (
        <>
          <SummaryGrid summary={summary || EMPTY_SUMMARY} />
          <Tabs defaultValue="applications" className="space-y-5">
            <TabsList className="h-auto flex-wrap justify-start">
              <TabsTrigger value="applications">Aplicaciones</TabsTrigger>
              {can.databases && <TabsTrigger value="databases">Bases de datos</TabsTrigger>}
              {can.reviewAccess && <TabsTrigger value="access">Solicitudes de acceso</TabsTrigger>}
            </TabsList>

            <TabsContent value="applications" className="space-y-4">
              <Card className="shadow-none">
                <CardHeader className="gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle>Inventario gobernado</CardTitle>
                    <CardDescription>{applications.length} aplicaciones vinculadas a iniciativas VIA.</CardDescription>
                  </div>
                  <div className="relative w-full sm:w-80">
                    <HugeiconsIcon
                      icon={Search01Icon}
                      size={16}
                      className="absolute left-3 top-2.5 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <Input
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Buscar aplicación, iniciativa u owner"
                      className="pl-9"
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-4">
                    <FilterSelect
                      value={initiativeFilter}
                      onValueChange={setInitiativeFilter}
                      placeholder="Todas las iniciativas"
                      options={initiatives.map((item) => ({ value: item.id, label: item.name || item.title }))}
                    />
                    <FilterSelect
                      value={ownerFilter}
                      onValueChange={setOwnerFilter}
                      placeholder="Todos los responsables"
                      options={ownerOptions.map((item) => ({ value: item.id, label: item.name }))}
                    />
                    <FilterSelect
                      value={environmentFilter}
                      onValueChange={setEnvironmentFilter}
                      placeholder="Todos los ambientes"
                      options={['dev', 'stg', 'prd'].map((item) => ({ value: item, label: item.toUpperCase() }))}
                    />
                    <FilterSelect
                      value={statusFilter}
                      onValueChange={setStatusFilter}
                      placeholder="Todos los estados"
                      options={[...new Set(applications.map((item) => item.status))].map((item) => ({
                        value: item,
                        label: item,
                      }))}
                    />
                  </div>
                  <ApplicationsTable
                    applications={filteredApplications}
                    canManage={can.manageApplications}
                    canRequest={can.requestAccess}
                    onChanged={load}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {can.databases && (
              <TabsContent value="databases">
                <DatabaseTable databases={databases} />
              </TabsContent>
            )}

            {can.reviewAccess && (
              <TabsContent value="access">
                <AccessTable requests={accessRequests} onChanged={load} />
              </TabsContent>
            )}
          </Tabs>
        </>
      )}
    </div>
  );
}

function SummaryGrid({ summary }) {
  const metrics = [
    ['Aplicaciones', summary.total_applications, CloudServerIcon],
    ['Con alertas', summary.applications_with_alert, AlertCircleIcon],
    ['Sin owner', summary.applications_without_owner, UserMultipleIcon],
    ['Aprobaciones pendientes', summary.pending_approvals, SecurityCheckIcon],
    ['Accesos activos', summary.active_access_sessions, Key01Icon],
    ['Despliegues recientes', summary.recent_deployments, Rocket01Icon],
    ['Costo mensual estimado', formatCurrency(summary.estimated_monthly_cost), DollarCircleIcon],
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {metrics.map(([label, value, icon]) => (
        <Card key={label} className="shadow-none">
          <CardContent className="flex items-center gap-4 p-5">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <HugeiconsIcon icon={icon} size={20} aria-hidden="true" />
            </span>
            <div>
              <p className="text-xl font-extrabold">{value}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ApplicationsTable({ applications, canManage, canRequest, onChanged }) {
  if (!applications.length) return <EmptyState icon={CloudServerIcon} text="No hay aplicaciones para mostrar." />;
  const provision = async (application, environment) => {
    try {
      await api.post(
        `/applications/${application.id}/provision`,
        { environment: environment.environment },
        { headers: { 'Idempotency-Key': `${application.id}-${environment.environment}-${Date.now()}` } },
      );
      toast.success(`Aprovisionamiento ${environment.environment.toUpperCase()} registrado`, {
        description: 'La operación quedó auditada y será procesada en ese ambiente.',
      });
      onChanged();
    } catch (error) {
      toast.error('No fue posible registrar la operación', { description: apiError(error) });
    }
  };
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Aplicación</TableHead>
          <TableHead>Registro</TableHead>
          <TableHead>Evaluación</TableHead>
          <TableHead>Owner</TableHead>
          <TableHead>Criticidad</TableHead>
          <TableHead>Ambientes</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Acción</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {applications.map((application) => {
          const nextEnvironment = (application.environments || []).find(
            (environment) => environment.provision_eligible && !environment.database,
          );
          const connectionEnvironments = (application.environments || []).filter(
            (environment) => environment.connection_available,
          );
          const blockedEnvironment = (application.environments || []).find(
            (environment) => !environment.connection_available && !environment.provision_eligible,
          );
          return (
            <TableRow key={application.id}>
              <TableCell>
                <p className="font-semibold">{application.name}</p>
                <p className="text-xs text-muted-foreground">{application.application_key}</p>
              </TableCell>
              <TableCell>
                <Link
                  to={`/iniciativas/${application.initiative_id}`}
                  className="font-medium text-primary hover:underline">
                  {application.initiative_code || application.initiative_name}
                </Link>
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <StatusBadge value={application.evaluation_status || 'Pendiente'} />
                  <span className="text-[11px] text-muted-foreground">Etapa: {application.initiative_stage}</span>
                  {!application.database_eligible && (
                    <p className="max-w-48 text-[11px] text-muted-foreground">
                      {application.database_eligibility_reason}
                    </p>
                  )}
                </div>
              </TableCell>
              <TableCell>{application.owner_name || 'Sin owner'}</TableCell>
              <TableCell>
                <StatusBadge value={application.criticality} />
              </TableCell>
              <TableCell>
                <EnvironmentBadges environments={application.environments} />
              </TableCell>
              <TableCell>
                <StatusBadge value={application.status} />
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" asChild>
                    <Link to={`/aplicaciones-ia/${application.id}`}>Administrar</Link>
                  </Button>
                  {canRequest && connectionEnvironments.length > 0 && (
                    <RequestAccessDialog application={application} onCreated={onChanged} />
                  )}
                  {canManage && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!nextEnvironment}
                      title={
                        nextEnvironment
                          ? nextEnvironment.provision_reason
                          : blockedEnvironment?.provision_reason || application.database_eligibility_reason
                      }
                      onClick={() => provision(application, nextEnvironment)}>
                      {nextEnvironment
                        ? `Crear BBDD ${nextEnvironment.environment.toUpperCase()}`
                        : connectionEnvironments.length > 0
                          ? 'Siguiente ambiente bloqueado'
                          : 'Sin BBDD habilitada'}
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function DatabaseTable({ databases }) {
  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle>PostgreSQL por ambiente</CardTitle>
        <CardDescription>Una instancia física por ambiente; separación lógica por aplicación.</CardDescription>
      </CardHeader>
      <CardContent>
        {!databases.length ? (
          <EmptyState icon={Database01Icon} text="Aún no hay recursos registrados." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ambiente</TableHead>
                <TableHead>Instancia</TableHead>
                <TableHead>Versión</TableHead>
                <TableHead>Conexión</TableHead>
                <TableHead>Bases lógicas</TableHead>
                <TableHead>Estado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {databases.map((database) => (
                <TableRow key={database.id}>
                  <TableCell>
                    <EnvironmentBadge value={database.environment} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{database.instance_identifier}</TableCell>
                  <TableCell>PostgreSQL {database.engine_version}</TableCell>
                  <TableCell>{database.proxy_endpoint ? 'RDS Proxy' : 'RDS directo'}</TableCell>
                  <TableCell>{database.logical_database_count}</TableCell>
                  <TableCell>
                    <StatusBadge value={database.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function AccessTable({ requests, onChanged }) {
  const decide = async (request, decision) => {
    try {
      await api.patch(`/access-requests/${request.id}/decision`, {
        decision,
        reason: decision === 'Aprobada' ? 'Aprobación desde VIA' : 'Rechazada por revisión de plataforma',
      });
      toast.success(decision === 'Aprobada' ? 'Acceso aprobado' : 'Solicitud rechazada');
      onChanged();
    } catch (error) {
      toast.error('No fue posible decidir la solicitud', { description: apiError(error) });
    }
  };
  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle>Solicitudes temporales</CardTitle>
        <CardDescription>Accesos a base de datos trazables, acotados y con expiración.</CardDescription>
      </CardHeader>
      <CardContent>
        {!requests.length ? (
          <EmptyState icon={Key01Icon} text="No hay solicitudes de acceso." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Solicitante</TableHead>
                <TableHead>Aplicación</TableHead>
                <TableHead>Ambiente</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Duración</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Decisión</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{request.requester_name}</TableCell>
                  <TableCell>{request.application_name}</TableCell>
                  <TableCell>
                    <EnvironmentBadge value={request.environment} />
                  </TableCell>
                  <TableCell>{request.access_role_key}</TableCell>
                  <TableCell>{request.requested_duration_minutes} min</TableCell>
                  <TableCell>
                    <StatusBadge value={request.status} />
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      {request.status === 'Pendiente' ? (
                        <>
                          <Button size="sm" onClick={() => decide(request, 'Aprobada')}>
                            Aprobar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => decide(request, 'Rechazada')}>
                            Rechazar
                          </Button>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">Resuelta</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function RequestAccessDialog({ application, onCreated }) {
  const availableEnvironments = (application.environments || []).filter((item) => item.connection_available);
  const [open, setOpen] = useState(false);
  const [environment, setEnvironment] = useState(availableEnvironments[0]?.environment || '');
  const [role, setRole] = useState('readonly');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('60');
  const [saving, setSaving] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post('/access-requests', {
        application_id: application.id,
        environment,
        access_role: role,
        reason,
        duration_minutes: Number(duration),
      });
      toast.success('Solicitud registrada', {
        description: 'El acceso requiere revisión y tendrá expiración automática.',
      });
      setOpen(false);
      setReason('');
      onCreated();
    } catch (error) {
      toast.error('No fue posible solicitar acceso', { description: apiError(error) });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Solicitar acceso
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Acceso temporal a {application.name}</DialogTitle>
          <DialogDescription>
            No se entregan contraseñas permanentes. Toda sesión queda asociada a esta solicitud.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Ambiente">
              <Choice
                value={environment}
                onValueChange={setEnvironment}
                values={availableEnvironments.map((item) => item.environment)}
              />
            </Field>
            <Field label="Rol">
              <Choice value={role} onValueChange={setRole} values={['readonly', 'readwrite', 'admin']} />
            </Field>
            <Field label="Duración">
              <Choice value={duration} onValueChange={setDuration} values={['30', '60', '120', '240']} />
            </Field>
          </div>
          <Field label="Motivo">
            <Textarea
              required
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Describe la tarea que requiere acceso"
            />
          </Field>
          <DialogFooter>
            <Button type="submit" disabled={saving}>
              {saving ? 'Solicitando…' : 'Enviar solicitud'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Choice({ value, onValueChange, values }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {values.map((item) => (
            <SelectItem key={item} value={item}>
              {item}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function FilterSelect({ value, onValueChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value="all">{placeholder}</SelectItem>
          {options
            .filter((item) => item.value)
            .map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
function EmptyState({ icon, text }) {
  return (
    <div className="flex min-h-36 flex-col items-center justify-center gap-3 rounded-xl border border-dashed text-sm text-muted-foreground">
      <HugeiconsIcon icon={icon} size={28} aria-hidden="true" />
      <p>{text}</p>
    </div>
  );
}
function LoadingState() {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 7 }, (_, index) => (
          <Skeleton key={index} className="h-24 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-96 rounded-xl" />
    </div>
  );
}
function formatCurrency(value) {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    value || 0,
  );
}
